/* Phase content for the demo.
 *
 * Each phase is short (5-9 seconds). Voice scripts are punchy, numbers-first,
 * and written for a buyer who hates marketing fluff. Total runtime: ~58s.
 *
 * The `screen` key drives the HTML mock that fills the foreground card.
 * The `scene` key drives the ambient Three.js background (just hue + intensity).
 */

import type { Lang } from './voice';

export type ScreenKey =
  | 'hook'         // prompt textarea, typewriter
  | 'fanout'       // prompt → 5 model logos
  | 'compare'      // 3 response cards side by side
  | 'synthesize'   // 3 cards collapse into one merged answer
  | 'optimal'      // a reusable prompt in a code block
  | 'pricing'      // 3 tier cards (Free / Pro / Team)
  | 'cta';         // final CTA

export type SceneKey = 'cool' | 'warm' | 'matrix' | 'gold' | 'coral';

export interface Phase {
  badge: Record<Lang, string>;
  caption: Record<Lang, string>;
  subcaption: Record<Lang, string>;
  voice: Record<Lang, string>;
  duration: number;
  screen: ScreenKey;
  scene: SceneKey;
}

export const PHASES: Phase[] = [
  {
    screen: 'hook',
    scene: 'matrix',
    duration: 6500,
    badge: {
      en: 'THE PITCH',
      es: 'LA PROPUESTA',
      pt: 'A PROPOSTA',
    },
    caption: {
      en: 'Your prompt. Five answers. Thirty seconds.',
      es: 'Tu prompt. Cinco respuestas. Treinta segundos.',
      pt: 'Seu prompt. Cinco respostas. Trinta segundos.',
    },
    subcaption: {
      en: 'Stop juggling tabs across Claude, GPT, Gemini, Grok and Mistral.',
      es: 'Deja de saltar entre pestañas de Claude, GPT, Gemini, Grok y Mistral.',
      pt: 'Pare de pular entre abas de Claude, GPT, Gemini, Grok e Mistral.',
    },
    voice: {
      en: 'Your prompt. Five answers. Thirty seconds. No more juggling tabs.',
      es: 'Tu prompt. Cinco respuestas. Treinta segundos. Se acabó saltar entre pestañas.',
      pt: 'Seu prompt. Cinco respostas. Trinta segundos. Acabou pular entre abas.',
    },
  },
  {
    screen: 'fanout',
    scene: 'matrix',
    duration: 8500,
    badge: {
      en: 'STEP 1 — FAN OUT',
      es: 'PASO 1 — ABANICO',
      pt: 'PASSO 1 — LEQUE',
    },
    caption: {
      en: 'One prompt. Sent to five models. In parallel.',
      es: 'Un prompt. Enviado a cinco modelos. En paralelo.',
      pt: 'Um prompt. Enviado a cinco modelos. Em paralelo.',
    },
    subcaption: {
      en: 'Claude is rigorous. GPT is creative. Gemini practical. Grok contrarian. Mistral fast.',
      es: 'Claude es riguroso. GPT creativo. Gemini práctico. Grok contrario. Mistral rápido.',
      pt: 'Claude é rigoroso. GPT criativo. Gemini prático. Grok contrário. Mistral rápido.',
    },
    voice: {
      en: 'You write one prompt. AI Board sends it to all five at once. Claude, GPT, Gemini, Grok, Mistral.',
      es: 'Escribes un prompt. AI Board lo envía a los cinco a la vez. Claude, GPT, Gemini, Grok, Mistral.',
      pt: 'Você escreve um prompt. O AI Board envia para os cinco de uma vez. Claude, GPT, Gemini, Grok, Mistral.',
    },
  },
  {
    screen: 'compare',
    scene: 'gold',
    duration: 9000,
    badge: {
      en: 'STEP 2 — COMPARE',
      es: 'PASO 2 — COMPARA',
      pt: 'PASSO 2 — COMPARE',
    },
    caption: {
      en: 'Read them side by side.',
      es: 'Léelas lado a lado.',
      pt: 'Leia lado a lado.',
    },
    subcaption: {
      en: 'Three reads in three seconds. The pattern emerges. You see who hedges.',
      es: 'Tres lecturas en tres segundos. El patrón emerge. Ves quién duda.',
      pt: 'Três leituras em três segundos. O padrão aparece. Você vê quem hesita.',
    },
    voice: {
      en: 'Three reads in three seconds. The pattern emerges. You see who hedges, who is bold, who is right.',
      es: 'Tres lecturas en tres segundos. El patrón emerge. Ves quién duda, quién se la juega, quién acierta.',
      pt: 'Três leituras em três segundos. O padrão aparece. Você vê quem hesita, quem ousa, quem acerta.',
    },
  },
  {
    screen: 'synthesize',
    scene: 'coral',
    duration: 9000,
    badge: {
      en: 'STEP 3 — SYNTHESIZE',
      es: 'PASO 3 — SINTETIZA',
      pt: 'PASSO 3 — SINTETIZE',
    },
    caption: {
      en: 'One click. One answer. Best of all of them.',
      es: 'Un clic. Una respuesta. Lo mejor de todas.',
      pt: 'Um clique. Uma resposta. O melhor de todas.',
    },
    subcaption: {
      en: 'Executive summary, pros and cons, consensus, custom — pick the lens.',
      es: 'Resumen ejecutivo, pros y contras, consenso, personalizado — elige el lente.',
      pt: 'Resumo executivo, prós e contras, consenso, personalizado — escolha a lente.',
    },
    voice: {
      en: 'One click. AI Board weaves the strongest threads from each into one answer. Executive summary, pros and cons, or your own format.',
      es: 'Un clic. AI Board teje los hilos más fuertes de cada una en una sola respuesta. Resumen ejecutivo, pros y contras, o tu propio formato.',
      pt: 'Um clique. O AI Board tece os fios mais fortes de cada uma em uma só resposta. Resumo executivo, prós e contras, ou seu próprio formato.',
    },
  },
  {
    screen: 'optimal',
    scene: 'matrix',
    duration: 9000,
    badge: {
      en: 'STEP 4 — REUSE',
      es: 'PASO 4 — REUSA',
      pt: 'PASSO 4 — REUSE',
    },
    caption: {
      en: 'Save the single-shot prompt. Skip the compare next time.',
      es: 'Guarda el prompt de un disparo. Sáltate el compare la próxima vez.',
      pt: 'Salve o prompt único. Pule o compare na próxima.',
    },
    subcaption: {
      en: 'AI Board reverse-engineers a reusable prompt from your synthesis.',
      es: 'AI Board hace ingeniería inversa de un prompt reutilizable desde tu síntesis.',
      pt: 'O AI Board faz engenharia reversa de um prompt reutilizável a partir da sua síntese.',
    },
    voice: {
      en: 'AI Board reverse engineers a single-shot prompt from your synthesis. Save it. Reuse it. Skip the compare for this task forever.',
      es: 'AI Board hace ingeniería inversa de un prompt único desde tu síntesis. Lo guardas. Lo reutilizas. Te saltas el compare para esa tarea para siempre.',
      pt: 'O AI Board faz engenharia reversa de um prompt único a partir da sua síntese. Você salva. Reusa. Pula o compare dessa tarefa para sempre.',
    },
  },
  {
    screen: 'pricing',
    scene: 'gold',
    duration: 9500,
    badge: {
      en: 'PRICING · HONEST',
      es: 'PRECIOS · HONESTOS',
      pt: 'PREÇOS · HONESTOS',
    },
    caption: {
      en: 'Free demo. Pro $19. Team $49.',
      es: 'Demo gratis. Pro $19. Team $49.',
      pt: 'Demo grátis. Pro $19. Team $49.',
    },
    subcaption: {
      en: 'Your keys stay in your browser. You pay each provider directly. Zero markup.',
      es: 'Tus llaves se quedan en tu navegador. Pagas a cada proveedor directo. Cero margen.',
      pt: 'Suas chaves ficam no seu navegador. Você paga a cada provedor direto. Zero margem.',
    },
    voice: {
      en: 'Free demo. Pro at nineteen dollars a month. Team at forty-nine. Your keys stay in your browser. You pay each provider directly. Zero markup.',
      es: 'Demo gratis. Pro a diecinueve dólares al mes. Team a cuarenta y nueve. Tus llaves se quedan en tu navegador. Pagas a cada proveedor directo. Cero margen.',
      pt: 'Demo grátis. Pro a dezenove dólares por mês. Team a quarenta e nove. Suas chaves ficam no seu navegador. Você paga a cada provedor direto. Zero margem.',
    },
  },
  {
    screen: 'cta',
    scene: 'matrix',
    duration: 7500,
    badge: {
      en: '14-DAY PRO TRIAL · NO CARD',
      es: 'PRO 14 DÍAS · SIN TARJETA',
      pt: 'PRO 14 DIAS · SEM CARTÃO',
    },
    caption: {
      en: 'One email. Trial starts today.',
      es: 'Un correo. La prueba empieza hoy.',
      pt: 'Um e-mail. O teste começa hoje.',
    },
    subcaption: {
      en: 'Cancel anytime. Your exports stay yours.',
      es: 'Cancela cuando quieras. Tus exportaciones son tuyas.',
      pt: 'Cancele quando quiser. Suas exportações são suas.',
    },
    voice: {
      en: 'Fourteen days. No card. Cancel anytime. Bring one email. We do the rest.',
      es: 'Catorce días. Sin tarjeta. Cancela cuando quieras. Trae un correo. Nosotros hacemos el resto.',
      pt: 'Quatorze dias. Sem cartão. Cancele quando quiser. Traga um e-mail. Nós fazemos o resto.',
    },
  },
];
